import { Inject, Injectable, DOCUMENT, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from '@core/api/api.service';
import { LogoService } from '@core/logo/logo.service';
import { Partner } from '@core/models/partner.model';
import { PartnerService } from '@core/partner/partner.service';
import { FormOption } from '@feature/forms/form-controls/form-option';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import tinycolor from 'tinycolor2';
import { BootstrapLoaderService } from '@core/loader/bootstrap-loader.service';
import { superUserRoute } from '@shared/shared-routes';

export type ThemeId = 'theme-default' | 'theme-partner';

export const themeMap = new Map<string, ThemeId>([
  ['capaccess', 'theme-default'],
  ['ready4impact', 'theme-partner'],
]);

export interface Theme {
  id: ThemeId;
  name: string;
}
interface Color {
  name: string;
  hex: string;
  darkContrast: boolean;
}
@Injectable({ providedIn: 'root' })
export class ThemeSwitcherService implements OnDestroy {
  private _themeList: FormOption[] = [];
  private _selectedTheme = new BehaviorSubject<ThemeId>('theme-default');
  private _activeTheme: BehaviorSubject<Partner> = new BehaviorSubject(null);
  activeTheme$ = this._activeTheme.asObservable();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private apiService: ApiService,
    private translate: TranslateService,
    private logoService: LogoService,
    private partnerService: PartnerService,
    private bootstrapLoader: BootstrapLoaderService,
    private router: Router,
  ) {
    const themes: Theme[] = [
      { id: 'theme-default', name: 'Default Theme' },
      { id: 'theme-partner', name: 'Partner Theme' },
    ];

    this._themeList = themes.map((t) => new FormOption(t.name, t.id));

    this._selectedTheme
      .asObservable()
      .pipe(
        tap((theme) => {
          themes.forEach((theme) => this.document.body.classList.remove(theme.id));
          this.document.body.classList.add(theme);
        }),
      )
      .subscribe();
  }

  public get allThemes(): FormOption[] {
    return this._themeList;
  }

  public get selectedTheme(): Observable<ThemeId> {
    return this._selectedTheme.asObservable();
  }

  public setSelectedTheme(theme: ThemeId) {
    this._selectedTheme.next(theme);
  }

  fetchTheme(options?: { resetToDomainTheme?: boolean }) {
    const currentUrl = this.document.location.href;
    let domainPayload = '';

    try {
      const url = new URL(currentUrl);
      domainPayload = `${url.protocol}//${url.hostname}`;
    } catch (error) {
      console.error('[ThemeSwitcher] Failed to extract domain from URL:', currentUrl, error);
      return;
    }

    if (!domainPayload) {
      console.error('[ThemeSwitcher] CRITICAL: domainPayload is empty - cannot fetch theme');
      return;
    }

    const formData = new FormData();
    formData.append('domain', domainPayload);

    const selectedPartnerUidFromKeys = this.readSelectedPartnerUidFromKeys();
    const selectedPartnerUid = options?.resetToDomainTheme ? null : selectedPartnerUidFromKeys;
    const previousActiveTheme = options?.resetToDomainTheme ? null : this.readStoredTheme('activeTheme');

    const request$ = this.bootstrapLoader.track('theme', this.apiService.post(`accounts/theme`, formData));
    request$.subscribe({
      next: (data: Partner) => {
        this.saveThemeToStorage('initialThemeParams', data);

        this.changeToPartnerTitleAndFevicon(data);
        this.replaceKapnativeBasedOnCondition(data);

        const shouldPreserveOrganizationTheme =
          !this.isOnSuperUserRoute() &&
          !!selectedPartnerUid &&
          !!previousActiveTheme?.uid &&
          previousActiveTheme.uid === selectedPartnerUid &&
          selectedPartnerUid !== data?.uid;

        if (shouldPreserveOrganizationTheme) {
          this.switchToOrganizationTheme(previousActiveTheme);
          return;
        }

        const shouldClearOrgSelectionKeys =
          !!selectedPartnerUidFromKeys &&
          selectedPartnerUidFromKeys !== data?.uid &&
          (!!options?.resetToDomainTheme || this.isOnSuperUserRoute());

        if (shouldClearOrgSelectionKeys) {
          this.clearOrganizationSelectionKeys();
        }

        localStorage.setItem('activeTheme', JSON.stringify(data));
        this.setActiveTheme(data);

        const partner = data;
        this.savePrimaryAndAccentColor(partner.primaryColor, partner.secondaryColor);
        this.saveSidebarColors(partner.sidebarBackgroundColor, partner.sidebarTextColor);
        this.saveLoginPageStyles(partner.loginPageImage?.url, partner.loginPageBackgroundColor);
        this.changeToPartnerTitleAndFevicon(partner);
        this.replaceKapnativeBasedOnCondition(partner);
        this.applyPartnerLogos(partner);
      },
      error: (error) => {
        console.error('[ThemeSwitcher] Failed to fetch theme:', {
          status: error?.status,
          statusText: error?.statusText,
          message: error?.message,
          url: error?.url,
          domain: domainPayload,
        });

        if (error?.status === 404) {
          this.router.navigate(['/user/404']);
          return;
        }

        // Apply fallback theme to ensure app can still function
        this.savePrimaryAndAccentColor('#41755a', '#cf933b');
        this.saveSidebarColors('#41755a', 'white');
        this.saveLoginPageStyles(null, null);
        let initialTheme = {
          buttonRounding: null,
          complianceTextDe: null,
          complianceTextEn: null,
          domain: domainPayload || 'localhost',
          financialUmbrella: 'AHP',
          fullCompanyName: 'Kapnative',
          id: null,
          impressumUrl: null,
          logoImageDark: null,
          logoImageLight: null,
          loginPageImage: null,
          loginPageBackgroundColor: null,
          primaryColor: '#41755a',
          privacyPolicyUrl: null,
          secondaryColor: '#cf933b',
          sidebarBackgroundColor: null,
          sidebarTextColor: null,
          termsAndConditionsUrl: null,
          theme: null,
          uid: null,
        } as Partner;

        this.saveThemeToStorage('initialThemeParams', initialTheme);
        this.changeToPartnerTitleAndFevicon(initialTheme);
        this.setActiveTheme(initialTheme);

        // Only show error snackbar if translations are loaded (to avoid showing translation keys)
        // The HttpErrorInterceptor will handle showing the error message
      },
    });
  }

  private readSelectedPartnerUidFromKeys(): string | null {
    try {
      const rawKeys = localStorage.getItem('keys');
      if (!rawKeys) {
        return null;
      }
      const partnerUid = JSON.parse(rawKeys)?.partneruid;
      return typeof partnerUid === 'string' && partnerUid.trim() ? partnerUid : null;
    } catch {
      return null;
    }
  }

  private readStoredTheme(storageKey: string): Partner | null {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) {
        return null;
      }
      return JSON.parse(raw) as Partner;
    } catch {
      return null;
    }
  }

  /**
   * Clears org-context fields written by companies overview / organization selector
   * when returning to the host/domain theme on superuser routes.
   */
  private clearOrganizationSelectionKeys(): void {
    try {
      const rawKeys = localStorage.getItem('keys');
      if (!rawKeys) {
        return;
      }

      const parsed = JSON.parse(rawKeys);
      if (!parsed || typeof parsed !== 'object') {
        localStorage.removeItem('keys');
        return;
      }

      const nextKeys = {
        ...parsed,
        emittentid: null,
        partneruid: null,
        isDueDiligence: null,
        isMarketplace: null,
      };

      const hasRemainingValues = Object.entries(nextKeys).some(
        ([key, value]) =>
          !['emittentid', 'partneruid', 'isDueDiligence', 'isMarketplace'].includes(key) &&
          value != null &&
          value !== '',
      );

      if (hasRemainingValues) {
        localStorage.setItem('keys', JSON.stringify(nextKeys));
      } else {
        localStorage.removeItem('keys');
      }
    } catch {
      localStorage.removeItem('keys');
    }
  }

  private isOnSuperUserRoute(): boolean {
    const routerUrl = this.router.url || '';
    const pathName = this.document.location?.pathname || '';
    const candidates = [routerUrl, pathName];
    return candidates.some(
      (url) =>
        url === `/${superUserRoute}` || url.startsWith(`/${superUserRoute}/`) || url.includes(`/${superUserRoute}?`),
    );
  }

  saveLoginPageStyles(imageUrl: string, backgroundColor: string) {
    if (imageUrl) {
      document.documentElement.style.setProperty('--theme-login-page-image', `url(${imageUrl})`);
    } else {
      document.documentElement.style.removeProperty('--theme-login-page-image');
    }

    if (backgroundColor) {
      document.documentElement.style.setProperty('--theme-login-page-background-color', backgroundColor);
    } else {
      document.documentElement.style.removeProperty('--theme-login-page-background-color');
    }
  }

  savePrimaryAndAccentColor(primaryColor?: string, accentColor?: string) {
    const dynamicColorPalette = [
      ...this.computeThemePalette(primaryColor || '#41755a', 'primary'),
      ...this.computeThemePalette(accentColor || '#cf933b', 'accent'),
    ];
    this.updateThemePalette(dynamicColorPalette);
  }

  saveSidebarColors(sidebarBackgroundColor: string, sidebarTextColor: string) {
    document.documentElement.style.setProperty('--theme-dynamic-sidebar-background', sidebarBackgroundColor);
    document.documentElement.style.setProperty('--theme-dynamic-sidebar-text', sidebarTextColor);
  }

  private updateThemePalette(colors: Color[]) {
    colors.forEach((color) => {
      document.documentElement.style.setProperty(`--theme-dynamic-palette-${color.name}`, color.hex);
      document.documentElement.style.setProperty(
        `--theme-dynamic-palette-contrast-${color.name}`,
        color.darkContrast ? 'rgba(black, 0.87)' : 'white',
      );
    });
  }

  private computeThemePalette(hex: string, type: 'primary' | 'accent'): Color[] {
    return [
      this.getColorObject(tinycolor(hex).lighten(55), `${type}-50`),
      this.getColorObject(tinycolor(hex).lighten(45), `${type}-100`),
      this.getColorObject(tinycolor(hex).lighten(35), `${type}-200`),
      this.getColorObject(tinycolor(hex).lighten(25), `${type}-300`),
      this.getColorObject(tinycolor(hex).lighten(15), `${type}-400`),
      this.getColorObject(tinycolor(hex), `${type}-500`),
      this.getColorObject(tinycolor(hex).darken(10), `${type}-600`),
      this.getColorObject(tinycolor(hex).darken(15), `${type}-700`),
      this.getColorObject(tinycolor(hex).darken(20), `${type}-800`),
      this.getColorObject(tinycolor(hex).darken(30), `${type}-900`),
      this.getColorObject(
        tinycolor(hex).isDark() ? tinycolor(hex).lighten(70) : tinycolor(hex).darken(70),
        `${type}-contrast`,
      ),
    ];
  }

  private getColorObject(value: tinycolor.Instance, name: string): Color {
    const c = tinycolor(value);
    return {
      name: name,
      hex: c.toHexString(),
      darkContrast: c.isLight(),
    };
  }

  ngOnDestroy(): void {
    this.setCustomColors();
  }

  setCustomColors() {
    document.documentElement.style.setProperty(`--theme-dynamic-palette-primary-50`, '#e8ecea');
    document.documentElement.style.setProperty(`--theme-dynamic-palette-primary-100`, '#bbc7c0');
    document.documentElement.style.setProperty(`--theme-dynamic-palette-primary-200`, '#8ea295');
    document.documentElement.style.setProperty(`--theme-dynamic-palette-primary-300`, '#617d6b');
    document.documentElement.style.setProperty(`--theme-dynamic-palette-primary-400`, '#519675');
    document.documentElement.style.setProperty(`--theme-dynamic-palette-primary-500`, '#41755a');
    document.documentElement.style.setProperty(`--theme-dynamic-palette-primary-600`, '#345841');
    document.documentElement.style.setProperty(`--theme-dynamic-palette-primary-700`, '#284e36');
    document.documentElement.style.setProperty(`--theme-dynamic-palette-primary-800`, '#1d452c');
    document.documentElement.style.setProperty(`--theme-dynamic-palette-primary-900`, '#032b12');

    document.documentElement.style.setProperty(`--theme-dynamic-palette-accent-50`, '#faf4eb');
    document.documentElement.style.setProperty(`--theme-dynamic-palette-accent-100`, '#f1dfc4');
    document.documentElement.style.setProperty(`--theme-dynamic-palette-accent-200`, '#e7c99d');
    document.documentElement.style.setProperty(`--theme-dynamic-palette-accent-300`, '#ddb376');
    document.documentElement.style.setProperty(`--theme-dynamic-palette-accent-400`, '#d49e4f');
    document.documentElement.style.setProperty(`--theme-dynamic-palette-accent-500`, '#cf933b');
    document.documentElement.style.setProperty(`--theme-dynamic-palette-accent-600`, '#b57921');
    document.documentElement.style.setProperty(`--theme-dynamic-palette-accent-700`, '#824600');
    document.documentElement.style.setProperty(`--theme-dynamic-palette-accent-800`, '#4f1300');
    document.documentElement.style.setProperty(`--theme-dynamic-palette-accent-900`, '#1c0000');
  }

  replaceKapnativeBasedOnCondition(partner: Partner) {
    // Get all available languages from partner data, or fallback to basic languages
    // Filter out languages with undefined codes
    const languages = partner.availableLanguages?.filter((lang) => lang && lang.code).map((lang) => lang.code) || [
      'en',
      'de',
    ];

    languages.forEach((lang) => {
      this.translate.getTranslation(lang).subscribe((translations) => {
        Object.keys(translations).forEach((key) => {
          if (translations[key].includes('Kapnative')) {
            if (key === 'INTL_ACKNOWLEDGE') {
              if (lang === 'en') {
                translations[key] =
                  'I acknowledge that Kapnative is a tied agent of AHP and is operating under its financial regulations';
              } else if (lang === 'de') {
                translations[key] =
                  'Ich habe verstanden, dass Kapnative als vertragliche gebundener Vermittler unter dem Haftungsdach der AHP Capital Management GmbH handelt';
              }
            }

            translations[key] = translations[key].replace('Kapnative', partner.fullCompanyName);
            translations[key] = translations[key].charAt(0).toUpperCase() + translations[key].slice(1);
          }

          if (translations[key].includes('AHP')) {
            translations[key] = translations[key].replace('AHP', partner.financialUmbrella);
            translations[key] = translations[key].charAt(0).toUpperCase() + translations[key].slice(1);
          }
        });

        this.translate.setTranslation(lang, translations, true);
      });
    });
  }

  changeToPartnerTitleAndFevicon(partner: Partner) {
    document.title = partner.fullCompanyName + '- Private Markets';
    const linkElements = document.querySelectorAll("link[rel*='icon']");
    if (partner.faviconImage?.url) {
      linkElements.forEach((linkElement: HTMLLinkElement) => {
        linkElement.href = partner.faviconImage.url;
      });
    }
  }

  saveThemeToStorage(key: string, themeParams: any) {
    localStorage.setItem(key, JSON.stringify(themeParams));
  }

  switchToOrganizationTheme(theme: Partner) {
    this.savePrimaryAndAccentColor(theme.primaryColor, theme.secondaryColor);
    this.saveSidebarColors(theme.sidebarBackgroundColor, theme.sidebarTextColor);
    this.saveLoginPageStyles(theme.loginPageImage?.url, theme.loginPageBackgroundColor);
    this.setActiveTheme(theme);
    this.applyPartnerLogos(theme);
  }

  /**
   * Sidebar/nav uses the dark logo. When only a light logo exists, reuse it so
   * the partner brand still appears after login.
   */
  private applyPartnerLogos(partner: Partner | Record<string, any>): void {
    const lightUrl = this.resolveLogoUrl(partner, 'light');
    const darkUrl = this.resolveLogoUrl(partner, 'dark') || lightUrl;
    this.logoService.setLogo(lightUrl, 'light');
    this.logoService.setLogo(darkUrl, 'dark');
  }

  /** Supports camelCase and snake_case theme payloads (raw API vs mapped Partner). */
  private resolveLogoUrl(partner: Partner | Record<string, any>, mode: 'light' | 'dark'): string | undefined {
    if (!partner) {
      return undefined;
    }
    const raw = partner as Record<string, any>;
    const file =
      mode === 'light' ? raw.logoImageLight ?? raw.logo_image_light : raw.logoImageDark ?? raw.logo_image_dark;
    if (!file) {
      return undefined;
    }
    return typeof file === 'string' ? file : file.url;
  }

  setActiveTheme(data) {
    if (!data) {
      // Handle case when data is undefined/null (e.g., during logout)
      return;
    }

    localStorage.setItem('activeTheme', JSON.stringify(data));
    this._activeTheme.next(data);
    this.partnerService.getStore().setActive(data.id);
  }
}
